import { useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import RefreshIcon from '@mui/icons-material/Refresh';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import BlockIcon from '@mui/icons-material/Block';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';

import { Button } from '@/atoms/Button';
import { Chip, type ChipStatusTone } from '@/atoms/Chip';
import { IconButton } from '@/atoms/IconButton';
import { Search } from '@/molecules/Search';
import { Menu, MenuItem, ListItemIcon, ListItemText } from '@/molecules/Menu';
import { DataTable, type MRT_ColumnDef } from '@/organisms/Table';
import { EmptyState } from '@/organisms/EmptyState';
import { FilterPopover, type FilterValue } from '@/organisms/FilterPopover';
import { TablePageLayout } from '@/templates/TablePageLayout';

/**
 * ThreatsListExample
 * A full threats list page composed entirely of Octopus UI components:
 * TablePageLayout + Search/FilterPopover toolbar + DataTable
 * with severity/status chips, a per-row action Menu, and an EmptyState when the
 * active search/filters match nothing.
 */
type Severity = 'critical' | 'high' | 'medium' | 'low';
type Status = 'active' | 'investigating' | 'contained';

interface Threat {
  id: string;
  name: string;
  path: string;
  entry: string;
  target: string;
  severity: Severity;
  status: Status;
  lastSeen: string;
}

const STATUS_LABEL: Record<Status, string> = {
  active: 'Active',
  investigating: 'Investigating',
  contained: 'Contained',
};

const STATUS_TONE: Record<Status, ChipStatusTone> = {
  active: 'error',
  investigating: 'warning',
  contained: 'success',
};

const ALL_THREATS: Threat[] = [
  {
    id: 'TP-1042',
    name: 'Public ALB to production database',
    path: 'Internet -> ALB -> EC2 -> RDS',
    entry: 'Public ALB',
    target: 'prod-db-01',
    severity: 'critical',
    status: 'active',
    lastSeen: '2m ago',
  },
  {
    id: 'TP-1038',
    name: 'Leaked key privilege escalation',
    path: 'IAM user -> AssumeRole -> Admin',
    entry: 'Leaked access key',
    target: 'admin-role',
    severity: 'critical',
    status: 'investigating',
    lastSeen: '14m ago',
  },
  {
    id: 'TP-1031',
    name: 'S3 public bucket data exfiltration',
    path: 'S3 public -> data exfil',
    entry: 's3-backups',
    target: 'PII bucket',
    severity: 'high',
    status: 'active',
    lastSeen: '1h ago',
  },
  {
    id: 'TP-1024',
    name: 'Exposed Kubernetes dashboard',
    path: 'Internet -> Ingress -> EKS dashboard',
    entry: 'Public ingress',
    target: 'eks-cluster',
    severity: 'high',
    status: 'investigating',
    lastSeen: '3h ago',
  },
  {
    id: 'TP-1019',
    name: 'Over-permissioned CI role',
    path: 'CI runner -> IAM -> S3 write',
    entry: 'CI pipeline',
    target: 'iam-role-ci',
    severity: 'medium',
    status: 'contained',
    lastSeen: '1d ago',
  },
  {
    id: 'TP-1007',
    name: 'Stale security group rule',
    path: 'SG 0.0.0.0/0 -> EC2 :22',
    entry: 'Open SSH',
    target: 'bastion-legacy',
    severity: 'low',
    status: 'contained',
    lastSeen: '3d ago',
  },
];

const SEVERITY_FILTER = {
  key: 'severity',
  label: 'Severity',
  type: 'multiselect' as const,
  options: [
    { label: 'Critical', value: 'critical' },
    { label: 'High', value: 'high' },
    { label: 'Medium', value: 'medium' },
    { label: 'Low', value: 'low' },
  ],
};

const STATUS_FILTER = {
  key: 'status',
  label: 'Status',
  type: 'multiselect' as const,
  options: [
    { label: 'Active', value: 'active' },
    { label: 'Investigating', value: 'investigating' },
    { label: 'Contained', value: 'contained' },
  ],
};

export function ThreatsListExample() {
  const [query, setQuery] = useState('');
  const [filters, setFilters] = useState<FilterValue>({});
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [activeThreat, setActiveThreat] = useState<Threat | null>(null);

  const data = useMemo(() => {
    const q = query.trim().toLowerCase();
    const severityValues = filters.severity?.values ?? [];
    const statusValues = filters.status?.values ?? [];
    return ALL_THREATS.filter((t) => {
      const matchesQuery =
        !q ||
        [t.id, t.name, t.path, t.entry, t.target].some((f) => f.toLowerCase().includes(q));
      const matchesSeverity = severityValues.length === 0 || severityValues.includes(t.severity);
      const matchesStatus = statusValues.length === 0 || statusValues.includes(t.status);
      return matchesQuery && matchesSeverity && matchesStatus;
    });
  }, [query, filters]);

  const openRowMenu = (event: React.MouseEvent<HTMLElement>, threat: Threat) => {
    setMenuAnchor(event.currentTarget);
    setActiveThreat(threat);
  };
  const closeRowMenu = () => {
    setMenuAnchor(null);
    setActiveThreat(null);
  };

  const columns = useMemo<MRT_ColumnDef<Threat>[]>(
    () => [
      {
        accessorKey: 'severity',
        header: 'Severity',
        size: 120,
        Cell: ({ cell }) => {
          const value = cell.getValue<Severity>();
          return <Chip purpose="severity" level={value} size="small" />;
        },
      },
      { accessorKey: 'id', header: 'ID', size: 100 },
      { accessorKey: 'name', header: 'Threat' },
      { accessorKey: 'path', header: 'Attack path' },
      { accessorKey: 'target', header: 'Target', size: 140 },
      {
        accessorKey: 'status',
        header: 'Status',
        size: 140,
        Cell: ({ cell }) => {
          const value = cell.getValue<Status>();
          return (
            <Chip purpose="status" tone={STATUS_TONE[value]} label={STATUS_LABEL[value]} size="small" />
          );
        },
      },
      { accessorKey: 'lastSeen', header: 'Last seen', size: 110 },
      {
        id: 'actions',
        header: 'Actions',
        size: 90,
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ row }) => (
          <IconButton
            icon={<MoreVertIcon fontSize="small" />}
            label={`Actions for ${row.original.name}`}
            size="small"
            onClick={(event) => openRowMenu(event, row.original)}
          />
        ),
      },
    ],
    [],
  );

  return (
    <Box sx={{ p: 3 }}>
      <TablePageLayout
        title="Threats"
        description="Active attack paths ranked by exploitability across connected cloud accounts."
        actions={
          <>
            <IconButton
              icon={<RefreshIcon fontSize="small" />}
              label="Refresh threats"
              tooltip="Refresh"
            />
            <IconButton
              icon={<FileDownloadIcon fontSize="small" />}
              label="Export threats"
              tooltip="Export"
            />
            <Button variant="destructive" startIcon={<TravelExploreIcon />}>
              Simulate attack
            </Button>
          </>
        }
        toolbar={
          <>
            <Box sx={{ width: 320 }}>
              <Search
                placeholder="Search threats, paths, targets..."
                onChange={setQuery}
              />
            </Box>
            <FilterPopover
              filters={[SEVERITY_FILTER, STATUS_FILTER]}
              onApply={setFilters}
              onReset={() => setFilters({})}
              showSelectAll
            />
          </>
        }
      >
        <Stack spacing={2}>
          {data.length > 0 ? (
            <DataTable columns={columns} data={data} enableRowSelection />
          ) : (
            <EmptyState
              icon={<TravelExploreIcon fontSize="inherit" />}
              title="No threats match your filters"
              description="Try clearing the search or resetting severity and status filters."
              action={
                <Button
                  variant="secondary"
                  onClick={() => {
                    setQuery('');
                    setFilters({});
                  }}
                >
                  Clear filters
                </Button>
              }
            />
          )}
        </Stack>
      </TablePageLayout>

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeRowMenu}>
        {activeThreat ? (
          <MenuItem disabled divider>
            <ListItemText
              primary={activeThreat.id}
              secondary={activeThreat.name}
            />
          </MenuItem>
        ) : null}
        <MenuItem onClick={closeRowMenu}>
          <ListItemIcon>
            <VisibilityIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View details</ListItemText>
        </MenuItem>
        <MenuItem onClick={closeRowMenu}>
          <ListItemIcon>
            <PlaylistAddCheckIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Assign to me</ListItemText>
        </MenuItem>
        <MenuItem onClick={closeRowMenu}>
          <ListItemIcon>
            <BlockIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Mark as contained</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
}
